// CloudBlobsOverlay.tsx

import React, { useMemo } from "react";
import "./CloudBlobsOverlay.scss";

type Range = [number, number];

export interface CloudBlobsOverlayProps {
    className?: string;
    seed?: string | number;
    count?: number;
    /** Blob length in % of container width */
    lengthPct?: Range; // e.g. [16, 38]
    /** Base thickness “scale” in vw (keeps proportions stable across screens) */
    thicknessPct?: Range; // e.g. [3, 7]
    rotationDeg?: Range; // e.g. [-22, 22]
    opacityRange?: Range; // e.g. [0.4, 0.75]
    blurPx?: number; // e.g. 28–36
    areaPaddingPct?: number; // e.g. 3
    maxAttemptsPerBlob?: number; // e.g. 80
    separationBias?: number; // e.g. 0.55
    verticalBandPct?: Range; // e.g. [0, 100]

    /**
     * 0..1 – how rounded each corner is (0=square, 1=fully round relative to local height).
     * Applied symmetrically to top/bottom of head and tail.
     */
    cornerRadiusPct?: number;

    /**
     * Range for percentage *difference* between head and tail thickness.
     * Example: [0.0, 0.35] => up to ±35% taper picked per blob; sign randomized.
     */
    headTailThicknessDeltaPct?: Range;

    /** Render prop override for a blob (advanced) */
    renderBlob?: (style: React.CSSProperties, i: number) => React.ReactNode;
}

/* ---------------------------- Seeded RNG helpers --------------------------- */
function xmur3(str: string) {
    let h = 1779033703 ^ str.length;
    for (let i = 0; i < str.length; i++) {
        h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    return function () {
        h = Math.imul(h ^ (h >>> 16), 2246822507);
        h = Math.imul(h ^ (h >>> 13), 3266489909);
        return (h ^= h >>> 16) >>> 0;
    };
}
function sfc32(a: number, b: number, c: number, d: number) {
    return function () {
        a >>>= 0;
        b >>>= 0;
        c >>>= 0;
        d >>>= 0;
        let t = (a + b) | 0;
        a = b ^ (b >>> 9);
        b = (c + (c << 3)) | 0;
        c = (c << 21) | (c >>> 11);
        d = (d + 1) | 0;
        t = (t + d) | 0;
        c = (c + t) | 0;
        return (t >>> 0) / 4294967296;
    };
}
function makeRng(seed: string | number) {
    const s = xmur3(String(seed));
    return sfc32(s(), s(), s(), s());
}
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/* ----------------------------- Blob generation ---------------------------- */
type BlobSpec = {
    xPct: number; // 0..100 (center)
    yPct: number; // 0..100 (center)
    lenPct: number; // width as % of container width
    rotDeg: number;
    opacity: number;

    headVW: number; // head thickness in vw
    tailVW: number; // tail thickness in vw
    maxVW: number; // max(headVW, tailVW)
    rHeadVW: number; // corner radius at head (vw)
    rTailVW: number; // corner radius at tail (vw)
};

export default function CloudBlobsOverlay({
    className,
    seed = "blobs-v3",
    count = 14,
    lengthPct = [16, 38],
    thicknessPct = [3, 6.5],
    rotationDeg = [-22, 22],
    opacityRange = [0.45, 0.75],
    blurPx = 32,
    areaPaddingPct = 3,
    maxAttemptsPerBlob = 80,
    separationBias = 0.55,
    verticalBandPct = [0, 100],
    cornerRadiusPct = 0.4, // NEW: roundness control (0..1)
    headTailThicknessDeltaPct = [0, 0.35], // NEW: taper range (0..1)
    renderBlob,
}: CloudBlobsOverlayProps) {
    const blobs = useMemo(() => {
        const rng = makeRng(seed);
        const placed: BlobSpec[] = [];
        const pad = areaPaddingPct;
        const yMin = Math.max(0, verticalBandPct[0]);
        const yMax = Math.min(100, verticalBandPct[1]);
        const cr = clamp(cornerRadiusPct, 0, 1);

        // rejection sampling to avoid overlaps
        const fits = (cand: BlobSpec) => {
            for (const b of placed) {
                const dx = Math.abs(cand.xPct - b.xPct) / 100;
                const dy = Math.abs(cand.yPct - b.yPct) / 100;

                // use lenPct + effective thickness (max of head/tail) as envelope
                const rc = Math.hypot(cand.lenPct, cand.maxVW) / 200;
                const rb = Math.hypot(b.lenPct, b.maxVW) / 200;
                const required =
                    separationBias * (rc + rb) +
                    (0.12 * Math.max(cand.maxVW, b.maxVW)) / 100;
                if (Math.hypot(dx, dy) < required) return false;
            }
            return true;
        };

        let attempts = 0;
        while (placed.length < count && attempts < count * maxAttemptsPerBlob) {
            attempts++;

            const len = lerp(lengthPct[0], lengthPct[1], rng());
            const baseThickVW = lerp(thicknessPct[0], thicknessPct[1], rng());
            const rot = lerp(rotationDeg[0], rotationDeg[1], rng());
            const op = lerp(opacityRange[0], opacityRange[1], rng());
            const x = lerp(pad, 100 - pad, rng());
            const y = lerp(
                Math.max(pad, yMin),
                Math.min(100 - pad, yMax),
                rng(),
            );

            // head/tail thickness taper
            const delta = lerp(
                headTailThicknessDeltaPct[0],
                headTailThicknessDeltaPct[1],
                rng(),
            );
            const sign = rng() < 0.5 ? -1 : 1;
            const headVW = baseThickVW * (1 + sign * delta);
            const tailVW = baseThickVW * (1 - sign * delta);
            const maxVW = Math.max(headVW, tailVW);

            // corner radii per side (relative to local height)
            let rHeadVW = (headVW / 2) * cr;
            let rTailVW = (tailVW / 2) * cr;

            const cand: BlobSpec = {
                xPct: x,
                yPct: y,
                lenPct: len,
                rotDeg: rot,
                opacity: op,
                headVW,
                tailVW,
                maxVW,
                rHeadVW,
                rTailVW,
            };

            if (fits(cand)) placed.push(cand);
        }

        return placed;
    }, [
        seed,
        count,
        lengthPct[0],
        lengthPct[1],
        thicknessPct[0],
        thicknessPct[1],
        rotationDeg[0],
        rotationDeg[1],
        opacityRange[0],
        opacityRange[1],
        areaPaddingPct,
        maxAttemptsPerBlob,
        separationBias,
        verticalBandPct[0],
        verticalBandPct[1],
        cornerRadiusPct,
        headTailThicknessDeltaPct[0],
        headTailThicknessDeltaPct[1],
    ]);

    // Ensure edge radii don't swallow the top/bottom edges
    const capEdgeRadii = (rLeft: number, rRight: number, total: number) => {
        const sum = rLeft + rRight;
        if (sum > total - 1) {
            const scale = (total - 1) / sum;
            return [rLeft * scale, rRight * scale] as const;
        }
        return [rLeft, rRight] as const;
    };

    return (
        <div
            className={`cloud-blobs-overlay${className ? " " + className : ""}`}
            aria-hidden
            style={{ "--cloud-blur": `${blurPx}px` } as React.CSSProperties}
        >
            {blobs.map((b, i) => {
                const style: React.CSSProperties = {
                    left: `${b.xPct}%`,
                    top: `${b.yPct}%`,
                    width: `${b.lenPct}%`,
                    height: `${b.maxVW}vw`,
                    transform: `translate(-50%, -50%) rotate(${b.rotDeg}deg)`,
                    opacity: b.opacity,
                };

                if (renderBlob)
                    return (
                        <React.Fragment key={i}>
                            {renderBlob(style, i)}
                        </React.Fragment>
                    );

                // Normalize all vw-based values into the SVG viewBox
                const vbW = 1000;
                const vbH = 1000;
                const cy = vbH / 2;

                const Hh = (b.headVW / b.maxVW) * vbH; // head height in vb units
                const Ht = (b.tailVW / b.maxVW) * vbH; // tail height in vb units
                const rH = (b.rHeadVW / b.maxVW) * vbH;
                const rT = (b.rTailVW / b.maxVW) * vbH;

                const x0 = 0,
                    x1 = vbW;
                const yTopHead = cy - Hh / 2;
                const yBottomHead = cy + Hh / 2;
                const yTopTail = cy - Ht / 2;
                const yBottomTail = cy + Ht / 2;

                // corner radii per edge (same for top/bottom on each side)
                let rHeadTop = rH,
                    rHeadBottom = rH,
                    rTailTop = rT,
                    rTailBottom = rT;

                // keep top/bottom edge lengths positive
                [rHeadTop, rTailTop] = capEdgeRadii(rHeadTop, rTailTop, vbW);
                [rHeadBottom, rTailBottom] = capEdgeRadii(
                    rHeadBottom,
                    rTailBottom,
                    vbW,
                );

                const d = [
                    // start at top-left after corner radius
                    `M ${x0 + rHeadTop} ${yTopHead}`,
                    // top edge to top-right before corner
                    `L ${x1 - rTailTop} ${yTopTail}`,
                    // top-right corner (clockwise)
                    `A ${rTailTop} ${rTailTop} 0 0 1 ${x1} ${
                        yTopTail + rTailTop
                    }`,
                    // right side down to before bottom-right corner
                    `L ${x1} ${yBottomTail - rTailBottom}`,
                    // bottom-right corner
                    `A ${rTailBottom} ${rTailBottom} 0 0 1 ${
                        x1 - rTailBottom
                    } ${yBottomTail}`,
                    // bottom edge to before bottom-left corner
                    `L ${x0 + rHeadBottom} ${yBottomHead}`,
                    // bottom-left corner
                    `A ${rHeadBottom} ${rHeadBottom} 0 0 1 ${x0} ${
                        yBottomHead - rHeadBottom
                    }`,
                    // left side up to before top-left corner
                    `L ${x0} ${yTopHead + rHeadTop}`,
                    // top-left corner
                    `A ${rHeadTop} ${rHeadTop} 0 0 1 ${
                        x0 + rHeadTop
                    } ${yTopHead}`,
                    "Z",
                ].join(" ");

                return (
                    <svg
                        key={i}
                        className="cloud-blobs-overlay__blob"
                        style={style}
                        viewBox={`0 0 ${vbW} ${vbH}`}
                        preserveAspectRatio="none"
                    >
                        <path d={d} />
                    </svg>
                );
            })}
        </div>
    );
}
